/**
 * Event dashboard metrics — counting rules
 *
 * All ticket counts are at the **order-item** level (one row = one ticket), never order count.
 *
 * Payment retry creates multiple Payment rows per order. FAILED / EXPIRED attempts are
 * superseded and ignored when classifying paid vs pending (same rules as
 * `calculateOrderStatus` / `buildPaymentSettlementExpressions`).
 *
 * Paid/free/pending use orderItem.total when set, otherwise bookableItem.price.
 * Deleted tickets are excluded from all counts.
 *
 * | Metric          | Rule |
 * |-----------------|------|
 * | Paid            | OrderItem CONFIRMED + PAID payment + paid-ticket price (total-first; see paidTicketPriceWhere) |
 * | Free sold       | OrderItem CONFIRMED + free-ticket price, or unset item total on a zero-payment order |
 * | Completed items | Paid + free sold tickets and entity registrations (no order.status === COMPLETED) |
 * | Pending         | OrderItem CONFIRMED + active AWAITING_PAYMENT + no PAID + paid ticket (item total > 0) |
 * | Failed payment  | OrderItem CONFIRMED + paid ticket + only FAILED/EXPIRED payments (no active retry) |
 * | Reserved        | OrderItem in RESERVED, APPROVED, or PENDING_APPROVAL (not CONFIRMED) |
 * | Available       | total ticket capacity − paid − free − pending − failed − reserved |
 *
 * Daily sales chart counts **tickets** (order items), grouped by order createdAt date.
 * Orders-by-status chart counts **orders** by OrderStatus.
 *
 * Checkout amounts: payment.grandTotal is the source of truth after payment creation.
 */

import {
  BookableItemConfigSource,
  OrderItemStatus,
  PaymentStatus,
  type Prisma,
} from "client";
import { SUPERSEDED_PAYMENT_STATUSES } from "services/virtualOrderStatus";

export const RESERVED_ORDER_ITEM_STATUSES: OrderItemStatus[] = [
  OrderItemStatus.RESERVED,
  OrderItemStatus.APPROVED,
  OrderItemStatus.PENDING_APPROVAL,
];

const confirmedEventTicketWhere = (
  eventId: string
): Prisma.OrderItemWhereInput => ({
  status: OrderItemStatus.CONFIRMED,
  ticket: {
    eventId,
    OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
  },
});

const confirmedEventEntityWhere = (
  eventId: string
): Prisma.OrderItemWhereInput => ({
  status: OrderItemStatus.CONFIRMED,
  entity: {
    entityTier: { eventId },
    OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
  },
});

const entityUsesOwnConfigWhere = (): Prisma.OrderItemWhereInput => ({
  entity: { configSource: BookableItemConfigSource.ENTITY },
});

const entityUsesTierConfigWhere = (): Prisma.OrderItemWhereInput => ({
  entity: {
    NOT: { configSource: BookableItemConfigSource.ENTITY },
  },
});

/** MongoDB: missing field is not the same as explicit null in all Prisma filters. */
export const orderItemTotalUnsetWhere = (): Prisma.OrderItemWhereInput => ({
  OR: [{ total: null }, { total: { isSet: false } }],
});

const bookableItemPriceUnsetOrFreeWhere = (): Prisma.OrderItemWhereInput => ({
  OR: [
    { ticket: { bookableItem: { price: null } } },
    { ticket: { bookableItem: { price: { isSet: false } } } },
    { ticket: { bookableItem: { price: { lte: 0 } } } },
  ],
});

/**
 * Item-level paid-ticket price — mirrors chart `isPaidTicket`:
 * use orderItem.total when set, otherwise bookableItem.price.
 */
export const paidTicketPriceWhere = (): Prisma.OrderItemWhereInput => ({
  OR: [
    { AND: [{ total: { isSet: true } }, { total: { gt: 0 } }] },
    {
      AND: [
        orderItemTotalUnsetWhere(),
        { ticket: { bookableItem: { price: { gt: 0 } } } },
      ],
    },
  ],
});

/**
 * Item-level free-ticket price — mirrors chart `isFreeTicket`:
 * use orderItem.total when set, otherwise bookableItem.price.
 * Mutually exclusive with {@link paidTicketPriceWhere}.
 */
export const freeTicketPriceWhere = (): Prisma.OrderItemWhereInput => ({
  OR: [
    { AND: [{ total: { isSet: true } }, { total: { lte: 0 } }] },
    {
      AND: [orderItemTotalUnsetWhere(), bookableItemPriceUnsetOrFreeWhere()],
    },
  ],
});

/**
 * Order-level free settlement — covers imports/legacy rows where orderItem.total
 * was never set but the order itself is free (zero total, no PAID payment).
 * Only used together with {@link orderItemTotalUnsetWhere} to avoid mixed carts.
 */
export const freeOrderWhere = (): Prisma.OrderItemWhereInput => ({
  order: {
    OR: [
      { total: 0 },
      { total: null },
      { total: { isSet: false } },
    ],
    NOT: { payments: { some: { status: PaymentStatus.PAID } } },
  },
});

/** Prisma filter: confirmed ticket order items for an event that are paid. */
export const paidTicketItemWhere = (
  eventId: string
): Prisma.OrderItemWhereInput => ({
  AND: [
    confirmedEventTicketWhere(eventId),
    paidTicketPriceWhere(),
    { order: { payments: { some: { status: PaymentStatus.PAID } } } },
  ],
});

/** Prisma filter: confirmed free ticket order items for an event. */
export const freeSoldTicketItemWhere = (
  eventId: string
): Prisma.OrderItemWhereInput => ({
  AND: [
    confirmedEventTicketWhere(eventId),
    {
      OR: [
        freeTicketPriceWhere(),
        {
          AND: [orderItemTotalUnsetWhere(), freeOrderWhere()],
        },
      ],
    },
  ],
});

const entityBookableItemPriceUnsetOrFreeWhere =
  (): Prisma.OrderItemWhereInput => ({
    OR: [
      {
        AND: [
          entityUsesOwnConfigWhere(),
          {
            OR: [
              { entity: { bookableItem: { price: null } } },
              { entity: { bookableItem: { price: { isSet: false } } } },
              { entity: { bookableItem: { price: { lte: 0 } } } },
            ],
          },
        ],
      },
      {
        AND: [
          entityUsesTierConfigWhere(),
          {
            OR: [
              { entity: { entityTier: { bookableItem: { price: null } } } },
              {
                entity: {
                  entityTier: { bookableItem: { price: { isSet: false } } },
                },
              },
              {
                entity: {
                  entityTier: { bookableItem: { price: { lte: 0 } } },
                },
              },
            ],
          },
        ],
      },
    ],
  });

/**
 * Item-level paid entity registration price — mirrors ticket paid rules using the
 * effective bookable item (entity override vs entity-tier).
 */
export const paidEntityPriceWhere = (): Prisma.OrderItemWhereInput => ({
  OR: [
    { AND: [{ total: { isSet: true } }, { total: { gt: 0 } }] },
    {
      AND: [
        orderItemTotalUnsetWhere(),
        {
          OR: [
            {
              AND: [
                entityUsesOwnConfigWhere(),
                { entity: { bookableItem: { price: { gt: 0 } } } },
              ],
            },
            {
              AND: [
                entityUsesTierConfigWhere(),
                {
                  entity: {
                    entityTier: { bookableItem: { price: { gt: 0 } } },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

/**
 * Item-level free entity registration price — mutually exclusive with
 * {@link paidEntityPriceWhere}.
 */
export const freeEntityPriceWhere = (): Prisma.OrderItemWhereInput => ({
  OR: [
    { AND: [{ total: { isSet: true } }, { total: { lte: 0 } }] },
    {
      AND: [
        orderItemTotalUnsetWhere(),
        entityBookableItemPriceUnsetOrFreeWhere(),
      ],
    },
  ],
});

/** Prisma filter: confirmed entity registration order items that are paid. */
export const paidEntityItemWhere = (
  eventId: string
): Prisma.OrderItemWhereInput => ({
  AND: [
    confirmedEventEntityWhere(eventId),
    paidEntityPriceWhere(),
    { order: { payments: { some: { status: PaymentStatus.PAID } } } },
  ],
});

/** Prisma filter: confirmed free entity registration order items. */
export const freeSoldEntityItemWhere = (
  eventId: string
): Prisma.OrderItemWhereInput => ({
  AND: [
    confirmedEventEntityWhere(eventId),
    {
      OR: [
        freeEntityPriceWhere(),
        {
          AND: [orderItemTotalUnsetWhere(), freeOrderWhere()],
        },
      ],
    },
  ],
});

/**
 * Sold / completed order items for overview cards — confirmed paid + free ticket
 * and entity registration items (no order.status === COMPLETED requirement).
 */
export const soldCompletedOrderItemWhere = (
  eventId: string
): Prisma.OrderItemWhereInput => ({
  OR: [
    paidTicketItemWhere(eventId),
    freeSoldTicketItemWhere(eventId),
    paidEntityItemWhere(eventId),
    freeSoldEntityItemWhere(eventId),
  ],
});

/**
 * Prisma filter: confirmed paid ticket order items with an active offline payment attempt.
 * Ignores superseded FAILED/EXPIRED siblings when checking for PAID (no PAID row exists).
 */
export const pendingTicketItemWhere = (
  eventId: string
): Prisma.OrderItemWhereInput => ({
  AND: [
    confirmedEventTicketWhere(eventId),
    paidTicketPriceWhere(),
    {
      order: {
        payments: { some: { status: PaymentStatus.AWAITING_PAYMENT } },
        NOT: { payments: { some: { status: PaymentStatus.PAID } } },
      },
    },
  ],
});

/**
 * Prisma filter: confirmed paid ticket order items whose payments are entirely superseded
 * (FAILED/EXPIRED) with no active retry — mirrors virtual status FAILED/EXPIRED.
 */
export const failedPaymentTicketItemWhere = (
  eventId: string
): Prisma.OrderItemWhereInput => ({
  AND: [
    confirmedEventTicketWhere(eventId),
    paidTicketPriceWhere(),
    {
      order: {
        payments: { some: {} },
        AND: [
          {
            NOT: {
              payments: {
                some: {
                  status: { notIn: SUPERSEDED_PAYMENT_STATUSES },
                },
              },
            },
          },
          {
            NOT: { payments: { some: { status: PaymentStatus.PAID } } },
          },
        ],
      },
    },
  ],
});

/** Prisma filter: ticket order items holding inventory but not yet sold. */
export const reservedTicketItemWhere = (
  eventId: string
): Prisma.OrderItemWhereInput => ({
  status: { in: RESERVED_ORDER_ITEM_STATUSES },
  ticket: {
    eventId,
    OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
  },
});
